import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getActiveWallets, submitPaymentConfirmation } from '../lib/firestoreService';

const CRYPTO_ICONS = {
  btc:    { symbol: '₿', color: '#f7931a', bg: 'rgba(247,147,26,0.12)', label: 'Bitcoin' },
  eth:    { symbol: 'Ξ', color: '#627eea', bg: 'rgba(98,126,234,0.12)', label: 'Ethereum' },
  usdt:   { symbol: '₮', color: '#26a17b', bg: 'rgba(38,161,123,0.12)', label: 'Tether' },
  ltc:    { symbol: 'Ł', color: '#bfbbbb', bg: 'rgba(191,187,187,0.12)', label: 'Litecoin' },
  bnb:    { symbol: '◆', color: '#f3ba2f', bg: 'rgba(243,186,47,0.12)', label: 'BNB' },
  xrp:    { symbol: '✦', color: '#00aae4', bg: 'rgba(0,170,228,0.12)', label: 'XRP' },
  bank:   { symbol: '🏦', color: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'Bank' },
  paypal: { symbol: 'P',  color: '#003087', bg: 'rgba(0,48,135,0.2)',  label: 'PayPal' },
};

export default function PaymentPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { shipment, clearance } = state ?? {};

  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [loadingWallets, setLoadingWallets] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [step, setStep] = useState('select'); // 'select' | 'confirm' | 'done'

  // Confirmation form
  const [txId, setTxId] = useState('');
  const [senderNote, setSenderNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!shipment || !clearance) { navigate('/'); return; }
    getActiveWallets().then(ws => {
      setWallets(ws);
      if (ws.length > 0) setSelectedWallet(ws[0]);
    }).finally(() => setLoadingWallets(false));
  }, []);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleConfirmSubmit = async (e) => {
    e.preventDefault();
    if (!txId.trim()) { setFormError('Transaction ID is required'); return; }
    setFormError('');
    setSubmitting(true);
    try {
      await submitPaymentConfirmation({
        shipmentId: shipment.id,
        trackingNumber: shipment.trackingNumber,
        clearanceFeeId: clearance.id,
        amount: clearance.amount,
        currency: clearance.currency,
        walletType: selectedWallet?.type ?? clearance.walletType,
        walletAddress: selectedWallet?.address ?? clearance.walletAddress,
        transactionId: txId.trim(),
        senderNote: senderNote.trim(),
      });
      setStep('done');
    } catch (err) {
      setFormError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getCrypto = (type) => CRYPTO_ICONS[type?.toLowerCase()] ?? CRYPTO_ICONS.bank;

  if (!shipment || !clearance) return null;

  return (
    <div className="min-h-screen" style={{ background: '#020818', fontFamily: "'Outfit', sans-serif", color: 'white' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;600&display=swap');

        * { box-sizing: border-box; }

        .slide-up {
          animation: slideUp 0.5s cubic-bezier(0.16,1,0.3,1) forwards;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .wallet-card {
          cursor: pointer;
          transition: all 0.2s;
          border: 2px solid rgba(255,255,255,0.06);
        }
        .wallet-card:hover {
          border-color: rgba(245,158,11,0.3);
          transform: translateY(-2px);
        }
        .wallet-card.selected {
          border-color: #f59e0b;
          background: rgba(245,158,11,0.06) !important;
        }

        input::placeholder { color: rgba(148,163,184,0.4); }
        input:focus { outline: none; border-color: #f59e0b !important; }
        textarea::placeholder { color: rgba(148,163,184,0.4); }
        textarea:focus { outline: none; border-color: #f59e0b !important; }
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5" style={{ background: 'rgba(2,8,24,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-yellow-400" style={{ color: '#94a3b8' }}>
            ← Back to Tracking
          </button>
          <a href="/" className="flex items-center gap-2">
            <img src="https://Atlasroutelogistics.com/storage/app/public/photos/logo/newlogo.png"
              alt="ATLASROUTELOGISTICS" className="h-8 brightness-0 invert" onError={e => e.target.style.display='none'} />
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-6">

        {/* ── Fee summary ── */}
        <div className="slide-up rounded-3xl overflow-hidden" style={{
          background: 'linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(15,32,64,0.8) 60%)',
          border: '1px solid rgba(245,158,11,0.2)',
        }}>
          <div className="p-6 md:p-10">
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#94a3b8' }}>Payment Required</p>
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl md:text-5xl font-black" style={{ color: '#f59e0b' }}>
                  {clearance.amount} <span className="text-2xl">{clearance.currency}</span>
                </h1>
                <p className="text-base font-medium mt-1" style={{ color: '#94a3b8' }}>{clearance.feeLabel}</p>
              </div>
              <div className="text-right">
                <p className="text-xs" style={{ color: '#94a3b8' }}>Tracking #</p>
                <p className="font-bold font-mono text-lg" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#f59e0b' }}>
                  {shipment.trackingNumber}
                </p>
                {clearance.dueDate && (
                  <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                    Due: <span className="text-white font-semibold">{clearance.dueDate}</span>
                  </p>
                )}
              </div>
            </div>
            {clearance.notes && (
              <div className="mt-4 p-4 rounded-2xl text-sm" style={{ background: 'rgba(255,255,255,0.04)', color: '#94a3b8' }}>
                📌 {clearance.notes}
              </div>
            )}
          </div>
        </div>

        {/* ── STEP: Done ── */}
        {step === 'done' && (
          <div className="slide-up rounded-3xl p-10 text-center" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center text-4xl mx-auto mb-6" style={{ background: 'rgba(16,185,129,0.15)' }}>
              ✅
            </div>
            <h2 className="text-3xl font-black text-emerald-400 mb-3">Payment Submitted!</h2>
            <p className="text-base mb-6" style={{ color: '#94a3b8' }}>
              Your payment confirmation has been received. Our team will verify it within <strong className="text-white">24–48 hours</strong> and release your shipment.
            </p>
            <div className="inline-block p-4 rounded-2xl mb-8 text-left" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-xs mb-1" style={{ color: '#94a3b8' }}>Transaction ID submitted</p>
              <p className="font-mono font-bold text-emerald-400">{txId}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 rounded-2xl font-bold text-sm"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                Back to Tracking
              </button>
              <button
                onClick={() => window.print()}
                className="px-6 py-3 rounded-2xl font-bold text-sm"
                style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }}
              >
                🖨️ Print Confirmation
              </button>
            </div>
          </div>
        )}

        {step !== 'done' && (
          <>
            {/* ── Wallet selection ── */}
            <div className="slide-up rounded-3xl p-6 md:p-8" style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <h2 className="text-lg font-bold mb-5">
                {step === 'select' ? '1.' : '✓'} Choose Payment Method
              </h2>
              {loadingWallets ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-20 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', animation: 'pulse 1.5s infinite' }} />
                  ))}
                </div>
              ) : wallets.length === 0 ? (
                <p className="text-sm" style={{ color: '#94a3b8' }}>No payment methods available. Contact support.</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {wallets.map(wallet => {
                    const ci = getCrypto(wallet.type);
                    const isSelected = selectedWallet?.id === wallet.id;
                    return (
                      <div
                        key={wallet.id}
                        className={`wallet-card rounded-2xl p-4 ${isSelected ? 'selected' : ''}`}
                        style={{ background: 'rgba(255,255,255,0.03)' }}
                        onClick={() => { setSelectedWallet(wallet); setCopied(false); setShowQR(false); }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold shrink-0"
                            style={{ background: ci.bg, color: ci.color }}>
                            {typeof ci.symbol === 'string' && ci.symbol.length <= 2 ? ci.symbol : '💳'}
                          </div>
                          <div>
                            <p className="font-bold text-sm">{wallet.label || ci.label}</p>
                            {wallet.network && <p className="text-xs" style={{ color: '#94a3b8' }}>{wallet.network}</p>}
                          </div>
                          {isSelected && <div className="ml-auto w-5 h-5 rounded-full flex items-center justify-center text-xs" style={{ background: '#f59e0b', color: '#020818' }}>✓</div>}
                        </div>
                        {wallet.minimumAmount && (
                          <p className="mt-2 text-xs" style={{ color: '#94a3b8' }}>
                            Min: {wallet.minimumAmount} {wallet.currency}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Selected wallet address & QR ── */}
            {selectedWallet && step === 'select' && (
              <div className="slide-up rounded-3xl p-6 md:p-8 space-y-5" style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <h2 className="text-lg font-bold">2. Send Payment</h2>

                {/* Amount reminder */}
                <div className="flex items-center justify-between p-4 rounded-2xl" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                  <span className="text-sm font-medium" style={{ color: '#94a3b8' }}>Send exactly:</span>
                  <span className="text-xl font-black" style={{ color: '#f59e0b' }}>{clearance.amount} {clearance.currency}</span>
                </div>

                {/* Address */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#94a3b8' }}>
                    {getCrypto(selectedWallet.type).label} Address
                    {selectedWallet.network && <span className="ml-2 normal-case text-yellow-400">({selectedWallet.network})</span>}
                  </p>
                  <div className="flex items-center gap-3 p-4 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <p className="flex-1 font-mono text-sm break-all leading-relaxed" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#e2e8f0' }}>
                      {selectedWallet.address}
                    </p>
                    <button
                      onClick={() => handleCopy(selectedWallet.address)}
                      className="shrink-0 px-4 py-2 rounded-xl text-xs font-bold transition-all"
                      style={{
                        background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                        color: copied ? '#10b981' : '#f59e0b',
                        border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)'}`,
                      }}
                    >
                      {copied ? '✓ Copied!' : '📋 Copy'}
                    </button>
                  </div>
                </div>

                {/* QR Code toggle */}
                {selectedWallet.qrCodeUrl && (
                  <div>
                    <button
                      onClick={() => setShowQR(!showQR)}
                      className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-yellow-400"
                      style={{ color: '#94a3b8' }}
                    >
                      <span>{showQR ? '▼' : '▶'}</span>
                      {showQR ? 'Hide QR Code' : 'Show QR Code'}
                    </button>
                    {showQR && (
                      <div className="mt-4 flex flex-col items-center gap-3">
                        <div className="p-4 rounded-2xl" style={{ background: 'white' }}>
                          <img src={selectedWallet.qrCodeUrl} alt="QR Code" className="w-48 h-48 object-contain" />
                        </div>
                        <p className="text-xs" style={{ color: '#94a3b8' }}>Scan with your wallet app</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Instructions */}
                {selectedWallet.instructions && (
                  <div className="p-4 rounded-2xl text-sm leading-relaxed" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', color: '#a5b4fc' }}>
                    ℹ️ {selectedWallet.instructions}
                  </div>
                )}

                {/* Warning */}
                <div className="p-4 rounded-2xl text-xs leading-relaxed" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#fca5a5' }}>
                  ⚠️ <strong>Important:</strong> Send only <strong>{clearance.currency}</strong> to this address. Sending the wrong currency or using the wrong network may result in permanent loss of funds.
                </div>

                {/* Proceed button */}
                <button
                  onClick={() => setStep('confirm')}
                  className="w-full py-4 rounded-2xl font-black text-base transition-all hover:scale-[1.02] hover:shadow-xl"
                  style={{ background: '#f59e0b', color: '#020818', boxShadow: '0 0 30px rgba(245,158,11,0.25)' }}
                >
                  I've Sent the Payment →
                </button>
              </div>
            )}

            {/* ── Confirm payment form ── */}
            {step === 'confirm' && (
              <div className="slide-up rounded-3xl p-6 md:p-8" style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <h2 className="text-lg font-bold mb-2">3. Confirm Your Payment</h2>
                <p className="text-sm mb-6" style={{ color: '#94a3b8' }}>
                  Enter your transaction details so our team can verify your payment quickly.
                </p>

                {/* Selected wallet recap */}
                {selectedWallet && (
                  <div className="flex items-center gap-3 p-4 rounded-2xl mb-6" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold"
                      style={{ background: getCrypto(selectedWallet.type).bg, color: getCrypto(selectedWallet.type).color }}>
                      {getCrypto(selectedWallet.type).symbol}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{selectedWallet.label || getCrypto(selectedWallet.type).label}</p>
                      <p className="font-mono text-xs truncate" style={{ color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>
                        {selectedWallet.address}
                      </p>
                    </div>
                    <button onClick={() => setStep('select')} className="text-xs px-3 py-1 rounded-lg hover:bg-white/10 transition-colors" style={{ color: '#94a3b8' }}>
                      Change
                    </button>
                  </div>
                )}

                <form onSubmit={handleConfirmSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Transaction / Hash ID <span className="text-red-400">*</span>
                    </label>
                    <input
                      value={txId}
                      onChange={e => setTxId(e.target.value)}
                      placeholder="e.g. 0xabc123... or txid..."
                      className="w-full px-4 py-3 rounded-xl text-sm transition-colors"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white',
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    />
                    <p className="mt-1 text-xs" style={{ color: '#94a3b8' }}>
                      Found in your wallet's transaction history or on a blockchain explorer.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Amount Sent <span className="text-xs font-normal" style={{ color: '#94a3b8' }}>(optional)</span>
                    </label>
                    <div className="flex gap-3">
                      <input
                        readOnly
                        value={`${clearance.amount} ${clearance.currency}`}
                        className="flex-1 px-4 py-3 rounded-xl text-sm"
                        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: '#f59e0b' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Additional Note <span className="text-xs font-normal" style={{ color: '#94a3b8' }}>(optional)</span>
                    </label>
                    <textarea
                      value={senderNote}
                      onChange={e => setSenderNote(e.target.value)}
                      placeholder="Any extra information for our team…"
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl text-sm resize-none"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white',
                      }}
                    />
                  </div>

                  {formError && (
                    <div className="p-3 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)' }}>
                      ⚠️ {formError}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setStep('select')}
                      className="px-5 py-3 rounded-2xl text-sm font-medium transition-colors hover:bg-white/5"
                      style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8' }}
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 py-3 rounded-2xl font-black text-base transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: '#f59e0b', color: '#020818' }}
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          Submitting…
                        </span>
                      ) : '✅ Confirm Payment'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t mt-10 py-8 text-center" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <p className="text-xs" style={{ color: '#94a3b8' }}>
          © {new Date().getFullYear()} ATLASROUTELOGISTICS · Secure Payment Portal ·{' '}
          <a href="mailto:atlasroutelogistics@kvm3.com" className="hover:text-yellow-400 transition-colors">atlasroutelogistics@kvm3.com</a>
        </p>
      </footer>
    </div>
  );
}